import {
  Box,
  Flex,
  FormControl,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import {
  Contact,
  contactsApi,
  UpdateContactRequest,
} from '@texas/api/endpoints/contactsApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { ErrorLabel } from '@texas/components/shared/ErrorLabel';
import { SubmitButton } from '@texas/components/shared/form/SubmitButton';
import { TexasFormLabel } from '@texas/components/shared/form/TexasFormLabel';
import { SharedDisclosureProps } from '@texas/components/shared/types';
import { request } from '@texas/utils/helpers/httpHelpers';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { EmptyDataButton } from '../shared/button/EmptyDataButton';
import { UserAvatar } from '../shared/users/UserAvatar';
import { SimpleUserAvatarButtons } from '../shared/users/SimpleUserAvatarButtons';
import { Icons } from '../shared/Icons';
import { SelectCustomerUserModal } from './SelectCustomerUserModal';
import { RoleRestrict } from '../shared/RoleRestrict';
import { Role } from '@texas/api/endpoints/userApi';
import { contactEvents } from './events';
import { CreateExternalUser } from '../users/CreateExternalUser';

export interface UpdateContactProps extends SharedDisclosureProps {
  contactId: number;
  onUpdated: (c: Contact) => void;
  customerId?: number;
  supplierId?: number;
  referenceName?: string;
}
export function EditContactModal({
  contactId,
  onUpdated,
  onClose,
  isOpen,
  customerId,
  supplierId,
  referenceName,
}: UpdateContactProps) {
  const { t } = useTranslation();

  return (
    <Modal onClose={onClose} isOpen={isOpen} closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalHeader fontWeight="bold">{t('contacts.editContact')}</ModalHeader>
        <ModalBody>
          <Form
            contactId={contactId}
            onClose={onClose}
            onUpdated={onUpdated}
            customerId={customerId}
            supplierId={supplierId}
            referenceName={referenceName}
          />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

function Form({
  contactId,
  onClose,
  onUpdated,
  customerId,
  supplierId,
  referenceName,
}: {
  contactId: number;
  onClose: () => void;
  onUpdated: (c: Contact) => void;
  customerId?: number;
  supplierId?: number;
  referenceName?: string;
}) {
  const toast = useToast();
  const { t } = useTranslation();
  const [persistedUserId, setPersistedUserId] = useState<number | null>();
  const [hasPendingUserRemoval, setHasPendingUserRemoval] = useState(false);
  const createUserRoles = [Role.SystemAdministrator];
  const {
    isOpen: isSelectUserOpen,
    onClose: onSelectUserClose,
    onOpen: onSelectUserOpen,
  } = useDisclosure();

  const {
    isOpen: isCreateUserOpen,
    onClose: onCreateUserClose,
    onOpen: onCreateUserOpen,
  } = useDisclosure();

  const {
    data: contact,
    refetch,
    loading,
    error,
    set,
  } = useApiResource(contactsApi.get);

  const { request: updateRequest, error: updateError } = useApiRequest(
    contactsApi.update,
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
    setValue,
  } = useForm<UpdateContactRequest>();

  useEffect(() => {
    setHasPendingUserRemoval(false);
    setPersistedUserId(undefined);
    refetch(contactId);
  }, [refetch, contactId]);

  useEffect(() => {
    if (contact && persistedUserId === undefined)
      setPersistedUserId(contact.user?.id ?? null);
  }, [contact, persistedUserId]);

  useEffect(() => {
    reset({
      name: contact?.name,
      email: contact?.email,
      userId: contact?.user?.id,
    });
  }, [reset, contact]);

  const onSubmit = async (data: UpdateContactRequest) =>
    await request(updateRequest, [contactId, data], (f) => {
      toast({
        title: t('general.updated'),
        status: 'success',
        isClosable: true,
      });
      if (persistedUserId)
        contactEvents.userLinksChanged.dispatch(persistedUserId);
      if (f.user && f.user.id !== persistedUserId)
        contactEvents.userLinksChanged.dispatch(f.user.id);
      onUpdated(f);
      onClose();
    });

  const setUser = useCallback(
    (id: number, name: string, email: string) => {
      setValue('userId', id);
      if (contact)
        set({
          ...contact,
          user: {
            id: id,
            name: name,
            email: email,
          },
        });
    },
    [contact, set, setValue],
  );

  return (
    <>
      {loading && <Spinner />}
      {error && <ErrorDetails error={error} />}
      {updateError && <ErrorDetails error={updateError} />}
      {!loading && contact && (
        <>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Flex direction="column" gap={4} pb={2}>
              <FormControl isRequired={true} isInvalid={!!errors.name}>
                <TexasFormLabel>{t('general.name')}</TexasFormLabel>
                <Input
                  {...register('name', {
                    required: true,
                  })}
                  variant="texas"
                  placeholder={t('general.name')}
                  autoFocus={true}
                />
                <ErrorLabel text={errors.name?.message} />
              </FormControl>
              <FormControl isRequired={true} isInvalid={!!errors.email}>
                <TexasFormLabel>{t('general.email')}</TexasFormLabel>
                <Input
                  type="email"
                  variant="texas"
                  {...register('email', {
                    required: true,
                  })}
                  placeholder={t('general.email')}
                />
                <ErrorLabel text={errors.email?.message} />
              </FormControl>

              {(customerId || supplierId) && (
                <>
                  <TexasFormLabel>{t('contacts.connectedUser')}</TexasFormLabel>
                  {contact.user ? (
                    <UserAvatar
                      name={contact.user.name}
                      email={contact.user.email}
                      label={null}
                      showTeams={false}
                      buttons={
                        <SimpleUserAvatarButtons
                          buttons={[
                            ...(customerId
                              ? [
                                  {
                                    tooltip: t('customerUser.change'),
                                    icon: <Icons.Pencil />,
                                    onClick: onSelectUserOpen,
                                  },
                                ]
                              : []),
                            {
                              tooltip: t('general.remove'),
                              icon: <Icons.Close />,
                              onClick: () => {
                                if (supplierId) setHasPendingUserRemoval(true);
                                setValue('userId', undefined);
                                set({ ...contact, user: undefined });
                              },
                            },
                          ]}
                        />
                      }
                    />
                  ) : hasPendingUserRemoval ? (
                    <Text role="status" fontSize="sm">
                      {t('contacts.saveUserRemovalBeforeLinking')}
                    </Text>
                  ) : (
                    <Flex gap={2}>
                      {customerId && (
                        <Box w="100%">
                          <EmptyDataButton
                            label={t('customerUser.select')}
                            description={t('customerUser.change')}
                            onClick={onSelectUserOpen}
                          />
                        </Box>
                      )}
                      <RoleRestrict allow={createUserRoles}>
                        <Box w="100%">
                          <EmptyDataButton
                            label={t('user.createUser')}
                            description={t('user.createUser')}
                            onClick={onCreateUserOpen}
                          />
                        </Box>
                      </RoleRestrict>
                    </Flex>
                  )}
                </>
              )}
              <SubmitButton loading={isSubmitting}>
                {t('general.update')}
              </SubmitButton>
            </Flex>
          </form>

          {customerId && (
            <SelectCustomerUserModal
              customerId={customerId}
              isOpen={isSelectUserOpen}
              onClose={onSelectUserClose}
              onSelect={(user) => {
                setUser(user.userId, user.userName, user.email);
              }}
            />
          )}
          {(customerId || supplierId) && !hasPendingUserRemoval && (
            <RoleRestrict allow={createUserRoles}>
              <CreateExternalUser
                role={customerId ? Role.Brand : Role.Supplier}
                customerId={customerId}
                referenceName={referenceName ?? ''}
                onClose={onCreateUserClose}
                isOpen={isCreateUserOpen}
                email={contact.email}
                name={contact.name}
                contactId={contact.id}
                onCreated={(user) => {
                  setUser(user.id, user.name, user.email);
                }}
              />
            </RoleRestrict>
          )}
        </>
      )}
    </>
  );
}
